"use client"

import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import { ClipLoader } from "react-spinners"
import { Button } from "@theme/components/Button"
import { SelectField, TextField } from "@theme/components/Fields"
import { unauthenticatedTRPC } from "../../trpc/trpc"

export function Form() {
    const [state, setState] = useState<
        "fresh" | "submitted" | "error" | "error-company-used" | "error-user-used" | "submitting"
    >("fresh")
    // Use useMemo and fetch to load the countries from /data/countries/en.json
    const [countries, setCountries] = useState<{ value: string; label: string }[] | undefined>(undefined)
    useEffect(() => {
        fetchCountries().then(setCountries)
    }, [])

    const organizationCreator = unauthenticatedTRPC.organizations.create.useMutation()

    const handleSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            const formData = new FormData(event.currentTarget)

            setState("submitting")

            organizationCreator
                .mutateAsync({
                    name: formData.get("name") as string,
                    email: formData.get("email") as string,
                    companyName: formData.get("company_name") as string,
                    country: formData.get("country") as string,
                })
                .then((response) => {
                    setState(response)
                })
                .catch(() => {
                    setState("error")
                })
        },
        [organizationCreator]
    )

    if (state === "submitting")
        return (
            <div>
                <div className="mx-auto mt-24 flex min-w-min flex-row justify-center">
                    <ClipLoader color="#088c96" loading={true} size={50} />
                </div>
            </div>
        )

    if (state === "fresh")
        return (
            <>
                <h1 className="mt-20 text-lg font-semibold text-gray-900">Get started today</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Already registered?{" "}
                    <Link href="/login" prefetch={false} className="text-primary-600 font-medium hover:underline">
                        Sign in
                    </Link>{" "}
                    to your account.
                </p>
                <form onSubmit={handleSubmit} className="mt-10 grid grid-cols-1 gap-y-8">
                    <TextField label="Your name" name="name" type="text" autoComplete="name" required />
                    <TextField
                        className="col-span-full"
                        label="Email address"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                    />
                    <TextField
                        className="col-span-full"
                        label="Company name"
                        name="company_name"
                        type="text"
                        autoComplete="company-name"
                        required
                    />
                    <SelectField label="Country" name="country">
                        <option value="">Select a country</option>
                        {countries?.map((country) => (
                            <option key={country.value} value={country.value}>
                                {country.label}
                            </option>
                        ))}
                    </SelectField>
                    <div className="col-span-full">
                        <Button type="submit" variant="solid" color="primary" className="w-full">
                            <span>
                                Sign up <span aria-hidden="true">&rarr;</span>
                            </span>
                        </Button>
                    </div>
                </form>
            </>
        )
    if (state === "submitted")
        return (
            <>
                <h1 className="mt-20 text-lg font-semibold text-gray-900">Invite sent</h1>
                <div className="mt-10 space-y-5 text-gray-700">
                    <p className="">Please check your inbox for an invite to complete your account setup.</p>
                    <p>
                        As our service is new, a lot of invite emails end up in spam. Please check your spam folder if
                        you don&apos;t see the invite in your inbox.
                    </p>
                </div>
            </>
        )
    if (state === "error-user-used")
        return (
            <>
                <h1 className="mt-20 text-lg font-semibold text-gray-900">User already registered.</h1>
                <p className="mt-10 text-gray-700">
                    Please sign in to your existing account:{" "}
                    <Link className="text-primary-600 font-medium hover:underline" href="/login">
                        Sign in
                    </Link>
                </p>
            </>
        )
    if (state === "error-company-used")
        return (
            <>
                <h1 className="mt-20 text-lg font-semibold text-gray-900">Company already registered.</h1>
                <p className="mt-10 text-gray-700">
                    Please contact the administrator of your company account to request an invite.
                </p>
            </>
        )
    return (
        <>
            <h1 className="mt-20 text-lg font-semibold text-gray-900">Something went wrong</h1>
            <p className="mt-10 text-gray-700">Please refresh this page and try again.</p>
        </>
    )
}

export async function fetchCountries(): Promise<{ value: string; label: string }[]> {
    const res = await fetch("/data/countries/en.json")
    const data = (await res.json()) as { value: string; label: string }[]
    return data
}
